import { useEffect, useState } from 'react'

type Props = {
  open: boolean
  onClose: () => void
  /** User's therapist license, pre-filled from profile. Read-only for audit. */
  therapistLicense: string | null
  /** Shown to the therapist so they see the provenance they're attesting to. */
  reviewFlagsLabel?: string
  /** Called when the user confirms. Parent fires POST /final. */
  onConfirm: () => Promise<void> | void
  submitting?: boolean
}

const ATTEST_COPY =
  'ฉันได้ตรวจและรับรองความถูกต้องของบันทึกนี้แล้ว — ข้อมูลที่บันทึกเป็นความจริงตามที่ได้ตรวจประเมินผู้ป่วย'

/**
 * Final attestation modal. Therapist must tick the confirmation checkbox
 * before the "เซ็นยืนยัน" button enables — matches Thai PT Council
 * (สภากายภาพบำบัด) documentation requirements for electronic signatures.
 *
 * The modal doesn't collect license manually — it's pulled from the
 * authenticated user profile, shown read-only so the therapist can verify
 * it's their own license before signing.
 */
export default function AttestationModal({
  open,
  onClose,
  therapistLicense,
  reviewFlagsLabel,
  onConfirm,
  submitting,
}: Props) {
  const [agreed, setAgreed] = useState(false)

  // Reset the checkbox each time the modal opens so an accidental re-open
  // can't submit twice in a row on a double-tap.
  useEffect(() => {
    if (open) setAgreed(false)
  }, [open])

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !submitting) onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose, submitting])

  if (!open) return null

  const canSubmit = agreed && !submitting && !!therapistLicense

  async function handleConfirm() {
    if (!canSubmit) return
    await onConfirm()
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-[2px] p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="attest-title"
      onClick={() => {
        if (!submitting) onClose()
      }}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-5 space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div>
          <h2 id="attest-title" className="text-lg font-semibold text-slate-900">
            เซ็นยืนยันบันทึก
          </h2>
          <p className="text-xs text-slate-500 mt-1">
            หลังยืนยันแล้ว บันทึกจะล็อกและไม่สามารถแก้ไขได้อีก
          </p>
        </div>

        <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-sm space-y-1">
          <div className="flex justify-between">
            <span className="text-slate-500">เลขที่ใบอนุญาต</span>
            <span className="font-mono font-medium">
              {therapistLicense || '— ไม่มีข้อมูล —'}
            </span>
          </div>
          {reviewFlagsLabel && (
            <div className="flex justify-between">
              <span className="text-slate-500">ธงตรวจสอบ</span>
              <span className="text-amber-700">{reviewFlagsLabel}</span>
            </div>
          )}
        </div>

        {!therapistLicense && (
          <div
            role="alert"
            className="rounded-md border border-rose-300 bg-rose-50 p-3 text-sm text-rose-800"
          >
            บัญชีของคุณยังไม่มีเลขที่ใบอนุญาต — ติดต่อผู้ดูแลระบบเพื่อเพิ่มก่อนเซ็นยืนยัน
          </div>
        )}

        <label className="flex gap-3 items-start cursor-pointer">
          <input
            type="checkbox"
            className="mt-1 h-5 w-5 accent-brand-600"
            checked={agreed}
            onChange={(e) => setAgreed(e.target.checked)}
            disabled={submitting}
          />
          <span className="text-sm text-slate-800 leading-relaxed">{ATTEST_COPY}</span>
        </label>

        <div className="flex gap-2 pt-2">
          <button
            type="button"
            className="btn-ghost flex-1 min-h-tap"
            onClick={onClose}
            disabled={submitting}
          >
            ยกเลิก
          </button>
          <button
            type="button"
            className="btn-primary flex-1 min-h-tap disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={handleConfirm}
            disabled={!canSubmit}
          >
            {submitting ? 'กำลังเซ็น…' : '✓ เซ็นยืนยัน'}
          </button>
        </div>
      </div>
    </div>
  )
}
